import { Star } from 'lucide-react'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { useCrossContextIndex } from '@/hooks/use-cross-context'
import { useRepoMeta } from '@/lib/query'
import type { CrossContextPlacement } from '@/lib/cross-context'

/** The contexts where this issue sits near the top, minus the one we're looking
 *  at (no point flagging "#1 in the backlog" while on the backlog), sorted best
 *  rank first. */
function useIssueFlags(issueId: string, excludeContext?: string): CrossContextPlacement[] {
  const placements = useCrossContextIndex().data?.get(issueId) ?? []
  return placements
    .filter((p) => p.context !== excludeContext)
    .sort((a, b) => a.rank - b.rank || a.label.localeCompare(b.label))
}

function lineFor(p: CrossContextPlacement, nameOf: (login: string) => string): string {
  if (p.kind === 'personal' && p.owner) return `#${p.rank} in ${nameOf(p.owner)}'s list`
  return `#${p.rank} in ${p.label}${p.by ? ` — ${nameOf(p.by)}` : ''}`
}

/**
 * Surfaces that an issue is manually prioritised in other contexts. `compact`
 * (rows, cards) is a star + count with the detail in a tooltip; `full` (the
 * sheet) is a labeled list. Renders nothing when there's nothing to flag.
 */
export function CrossContextFlags({
  issueId,
  excludeContext,
  variant = 'compact',
}: {
  issueId: string
  excludeContext?: string
  variant?: 'compact' | 'full'
}) {
  const flags = useIssueFlags(issueId, excludeContext)
  const meta = useRepoMeta().data
  if (flags.length === 0) return null

  const nameOf = (login: string) => meta?.assignableUsers.find((u) => u.login === login)?.name ?? login

  if (variant === 'full') {
    return (
      <section>
        <h3 className="text-muted-foreground mb-1.5 flex items-center gap-1.5 text-[11px] font-semibold tracking-wide uppercase">
          <Star className="size-3" />
          Prioritised elsewhere
        </h3>
        <div className="flex flex-col gap-1">
          {flags.map((p) => (
            <span key={`${p.context}:${p.group}:${p.rank}`} className="flex items-center gap-1.5 text-xs">
              <Star className="size-3 shrink-0 fill-amber-500 text-amber-500" />
              {lineFor(p, nameOf)}
            </span>
          ))}
        </div>
      </section>
    )
  }

  // The visual star+count is a hover/mouse affordance (Tooltip), so the detail
  // is keyboard/AT-invisible — carry the full summary in screen-reader text on
  // the card itself.
  const summary = `Also prioritised — ${flags.map((p) => lineFor(p, nameOf)).join('; ')}`

  return (
    <>
      <span className="sr-only">{summary}</span>
      <Tooltip>
        <TooltipTrigger asChild>
          <span
            aria-hidden
            className="inline-flex shrink-0 items-center gap-0.5 text-[10.5px] font-medium text-amber-500"
          >
            <Star className="size-3 fill-current" />
            {flags.length}
          </span>
        </TooltipTrigger>
        <TooltipContent side="top" className="flex flex-col gap-0.5">
          {flags.map((p) => (
            <span key={`${p.context}:${p.group}:${p.rank}`}>{lineFor(p, nameOf)}</span>
          ))}
        </TooltipContent>
      </Tooltip>
    </>
  )
}
